// components/roles/RoleCard.tsx
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Lock, Users, Shield } from "lucide-react";
import { Role } from "@/types/roles";

// Función helper para contar permisos (ahora siempre array de strings)
const getPermissionsCount = (permissions: Role["permissions"]): number => {
  if (Array.isArray(permissions)) {
    return permissions.length;
  }
  if (typeof permissions === "object" && permissions !== null) {
    if ((permissions as any).all) return 999;
    return Object.values(permissions).filter((v) => v === true).length;
  }
  return 0;
};

// Función para mostrar el conteo de permisos de manera amigable
const formatPermissionsCount = (count: number): string => {
  if (count >= 999) {
    return "Todos los permisos";
  }
  return `${count} ${count === 1 ? "permiso" : "permisos"}`;
};

interface RoleCardProps {
  role: Role;
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
}

export function RoleCard({ role, onEdit, onDelete }: RoleCardProps) {
  // Determinar si el rol está activo (asumiendo que si no tiene is_active o es true)
  const isActive = role.is_active !== false;

  // Obtener conteo de permisos
  const permissionsCount = getPermissionsCount(role.permissions);
  const permissionsText = formatPermissionsCount(permissionsCount);

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            {role.is_system_role && <Lock className="h-4 w-4 text-slate-400" />}
            <CardTitle className="text-lg font-semibold">{role.name}</CardTitle>
          </div>
          <Badge
            className={
              isActive
                ? "bg-green-100 text-green-800"
                : "bg-slate-100 text-slate-800"
            }
          >
            {isActive ? "Activo" : "Inactivo"}
          </Badge>
        </div>
        {role.is_system_role && (
          <Badge variant="outline" className="text-xs mt-1"></Badge>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600 line-clamp-2">
          {role.description || "Sin descripción"}
        </p>

        <div className="flex items-center justify-between text-sm text-slate-600">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <Shield className="h-4 w-4 mr-1" />
              <span>{permissionsText}</span>
            </div>
            <div className="flex items-center">
              <Users className="h-4 w-4 mr-1" />
              <span>{role.users_count || 0} usuarios</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(role)}
            disabled={role.is_system_role}
            className="text-blue-600 hover:text-blue-700"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(role)}
            disabled={role.is_system_role || !isActive}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
